import { useMemo } from 'react';
import { getTreeMapDisplayText } from '../../util/formatterFunctions';

function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return [70, 131, 230];
  return [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16),
  ];
}

const calculateColorShade = (
  value: number,
  maxValue: number,
  primaryColor: string
): string => {
  const normalizedValue = value / maxValue;
  const [pr, pg, pb] = hexToRgb(primaryColor);

  // Light shade: blend primary toward white
  const lightR = Math.round(pr + (255 - pr) * 0.55);
  const lightG = Math.round(pg + (255 - pg) * 0.55);
  const lightB = Math.round(pb + (255 - pb) * 0.55);

  // Dark shade: blend primary toward black
  const darkR = Math.round(pr * 0.45);
  const darkG = Math.round(pg * 0.45);
  const darkB = Math.round(pb * 0.45);

  const r = Math.round(lightR - normalizedValue * (lightR - darkR));
  const g = Math.round(lightG - normalizedValue * (lightG - darkG));
  const b = Math.round(lightB - normalizedValue * (lightB - darkB));

  return `rgb(${r},${g},${b})`;
};

export const CustomizedContent = (props: {
  root: any;
  depth: number;
  x: number;
  y: number;
  width: number;
  height: number;
  index: number;
  payload: any;
  colors: string[];
  rank: number;
  name: string;
  onClick: any;
  selectedCell: string;
  value: any;
  enableDarkTheme?: boolean;
  totalCost: number;
  primaryColor?: string;
}) => {
  const {
    root,
    depth,
    x,
    y,
    width,
    height,
    name,
    onClick,
    selectedCell,
    value,
    enableDarkTheme = false,
    totalCost,
    primaryColor = '#4683E6',
  } = props;
  const padding = 2;
  const handleClick = (event: any) => {
    onClick(event, name);
  };

  const displayText = useMemo(() => {
    const temp = getTreeMapDisplayText(name, width, height);
    return temp;
  }, [width, height, name]);

  return (
    <g onClick={handleClick}>
      <rect
        x={x}
        y={y}
        width={width - 2 * padding}
        height={height - 2 * padding}
        style={{
          fill: calculateColorShade(
            value,
            root?.children
              ? Math.max(...root.children.map((child: any) => child.value))
              : value,
            primaryColor
          ),
          strokeDasharray: selectedCell === name ? '4 2' : 'none',
          stroke:
            selectedCell === name
              ? '#FFA726'
              : enableDarkTheme
              ? '#0A192D'
              : '#ffffff',
          strokeWidth: selectedCell === name ? 4 : 2,
          strokeOpacity: selectedCell === name ? 1 : 0,
        }}
        rx={4}
        ry={4}
      />
      {depth === 1 ? (
        <text
          x={x + width / 2}
          y={y + height / 2 + 7}
          textAnchor="middle"
          fill="#fff"
          fontSize={14}
        >
          {displayText}
        </text>
      ) : null}
      {depth === 1 ? (
        <text x={x + 4} y={y + 18} fill="#fff" fontSize={16} fillOpacity={0.9}>
          {convertAmount(totalCost)}
        </text>
      ) : null}
    </g>
  );
};

function convertAmount(amount: number) {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(2)}m`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(2)}k`;
  } else {
    return amount.toString();
  }
}
